import { QuantitySelectorComponent } from '@theme/component-quantity-selector';

/**
 * A custom element that allows the user to select a quantity in the cart.
 * Extends QuantitySelectorComponent but uses absolute max limits instead of effective max.
 * Semantics: "What should the total quantity BE in the cart" vs "How many to ADD to cart"
 *
 * @extends {QuantitySelectorComponent}
 */
class CartQuantitySelectorComponent extends QuantitySelectorComponent {
  /**
   * Gets the effective maximum value for cart quantity selector
   * Cart page: uses absolute max (how much can be in cart total)
   * @returns {number | null} The effective max, or null if no max
   */
  getEffectiveMax() {
    const { max } = this.getCurrentValues();
    return max; // Cart uses absolute max, not max minus cart quantity
  }

  /**
   * Whether the minus button doubles as a remove button at the minimum quantity.
   * Set by the `remove_at_min` param on the quantity-selector snippet, which the
   * cart line item passes and the quick order list does not.
   * @returns {boolean}
   */
  get removesAtMin() {
    return this.hasAttribute('data-remove-at-min');
  }

  /**
   * Updates button states based on current value and limits
   * Cart buttons are always managed client-side, never server-disabled
   */
  updateButtonStates() {
    const { minusButton, plusButton } = this.refs;
    const { min, value } = this.getCurrentValues();
    const effectiveMax = this.getEffectiveMax();
    const atMin = value <= min;

    // Cart buttons are always dynamically managed. When the minus doubles as
    // remove it stays enabled at the minimum, because that is the click that
    // removes the line.
    minusButton.disabled = atMin && !this.removesAtMin;
    // Drives the icon and label swap in cart-products.liquid.
    this.toggleAttribute('data-at-min', atMin && this.removesAtMin);
    plusButton.disabled = effectiveMax !== null && value >= effectiveMax;
  }

  /**
   * Handles the quantity decrease event.
   * At the minimum quantity this removes the line instead of decrementing.
   * @param {Event} event - The event.
   */
  decreaseQuantity(event) {
    const { min, value } = this.getCurrentValues();

    if (!this.removesAtMin || value > min) return super.decreaseQuantity(event);
    if (!(event.target instanceof HTMLElement)) return;

    event.preventDefault();

    // `updateQuantity` clamps to min, so the removal writes the input directly.
    // Quantity 0 is what CartItemsComponent reads as "remove this line".
    this.refs.quantityInput.value = '0';
    this.onQuantityChange();
  }
}

if (!customElements.get('cart-quantity-selector-component')) {
  customElements.define('cart-quantity-selector-component', CartQuantitySelectorComponent);
}
